'use client';

import { useUnit } from 'effector-react';
import { useEffect, useMemo } from 'react';
import './ThemeRuntime.module.scss';

import { GlobalAppToastHost } from '@/components/ui/GlobalAppToastHost';
import { DEFAULT_THEME_SELECTION, THEME_STORAGE_KEY, type ThemeSelection } from '@/features/theme/logic';
import { $resolvedTheme, $themeSelection, systemPreferenceChanged, themeSelectionChanged, themeSelectionHydrated } from '@/features/theme/model';
import { createChattengerMuiTheme } from '@/features/theme/muiTheme';
import { getDocumentOrNull, getWindowOrNull } from '@/lib/browser/runtime';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

const getStoredSelection = (): ThemeSelection => {
    const browserWindow = getWindowOrNull();
    if (!browserWindow) {
        return DEFAULT_THEME_SELECTION;
    }

    const value = browserWindow.localStorage.getItem(THEME_STORAGE_KEY);

    return value === 'light' || value === 'dark' || value === 'system' ? value : DEFAULT_THEME_SELECTION;
};

export const ThemeRuntime = ({ children }: { children: React.ReactNode }) => {
    const [themeSelection, resolvedTheme] = useUnit([$themeSelection, $resolvedTheme]);
    const muiTheme = useMemo(() => createChattengerMuiTheme(resolvedTheme), [resolvedTheme]);

    useEffect(() => {
        const browserWindow = getWindowOrNull();
        if (!browserWindow) {
            return;
        }

        const initialSelection = getStoredSelection();
        themeSelectionHydrated(initialSelection);
        systemPreferenceChanged(browserWindow.matchMedia('(prefers-color-scheme: dark)').matches);

        const mediaQuery = browserWindow.matchMedia('(prefers-color-scheme: dark)');
        const onMediaChange = (event: MediaQueryListEvent) => {
            systemPreferenceChanged(event.matches);
        };

        mediaQuery.addEventListener('change', onMediaChange);

        return () => {
            mediaQuery.removeEventListener('change', onMediaChange);
        };
    }, []);

    useEffect(() => {
        const browserDocument = getDocumentOrNull();
        const browserWindow = getWindowOrNull();
        if (!browserDocument || !browserWindow) {
            return;
        }

        browserDocument.documentElement.dataset.themeSelection = themeSelection;
        browserDocument.documentElement.dataset.theme = resolvedTheme;
        browserDocument.documentElement.style.colorScheme = resolvedTheme;

        try {
            browserWindow.localStorage.setItem(THEME_STORAGE_KEY, themeSelection);
        } catch {
            // Best effort only.
        }
    }, [resolvedTheme, themeSelection]);

    return (
        <MuiThemeProvider theme={muiTheme}>
            <CssBaseline />
            <GlobalAppToastHost />
            {children}
        </MuiThemeProvider>
    );
};

export const updateThemeSelection = (selection: ThemeSelection) => {
    themeSelectionChanged(selection);
};
